import React, { useState, useCallback, useRef } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonPage,
  IonList,
  IonItem,
  IonIcon,
  useIonRouter,
  IonCard,
  IonCardContent,
  IonGrid,
  IonButton,
  IonLabel,
  IonModal
} from '@ionic/react';
import {
  calendarOutline,
  alertCircleOutline,
  mailOutline,
  documentTextOutline,
  settingsOutline,
  calculatorOutline,
  statsChartOutline,
  receiptOutline,
  timerOutline,
  chevronForwardOutline,
  chevronBack,
  warningOutline,
  walletOutline,
  constructOutline,
  searchOutline,
} from 'ionicons/icons';
import styles from './Billing.module.scss';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import SearchModal from '../components/SearchModal';
import CreateInvoice from './CreateInvoice';
import InvoiceList from './InvoiceList';
import EstimateList from './EstimateList';
import ServiceOrderList from './ServiceOrderList';
import PriceCalculator from './PriceCalculator';
import HelcimSubscriptionsModal from '../components/HelcimSubscriptionsModal';
import HelcimPlansModal from '../components/HelcimPlansModal';
import HelcimPaymentsModal from '../components/HelcimPaymentsModal';
import { useTranslation } from 'react-i18next';

const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const containerRef = useRef<HTMLIonGridElement>(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showInvoiceList, setShowInvoiceList] = useState(false);
  const [showEstimateList, setShowEstimateList] = useState(false);
  const [showServiceOrders, setShowServiceOrders] = useState(false);
  const [showPriceCalculator, setShowPriceCalculator] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [showPayments, setShowPayments] = useState(false);

  const handleClose = useCallback(() => {
    if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/manage');
    }
  }, [router]);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(containerRef, 66, applyProgress);


  const pageContent = (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={t('pages.Billing.companyBilling')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        <IonButton shape='round' className={styles.backButton} onClick={handleClose}>
          <IonIcon icon={chevronBack} />
        </IonButton>
      }
      endButtons={
        <IonButton shape='round' className={styles.searchButton} onClick={() => setShowSearchModal(true)}>
          <IonIcon icon={searchOutline} />
        </IonButton>
      }
    >
      <IonContent className={styles.content}>
        <IonGrid
          ref={containerRef}
          className={styles.billingContainer}
          onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop)}
        >
          {/* Hero — same collapsing-title-on-scroll animation as Profile.tsx's
              TitleCard, but with cardClassName styled fully transparent (matches
              Profile's .profileCard recipe) so it has no visible card background. */}
          <TitleCard
            ref={titleCardAnimationRef}
            title={t('pages.Billing.companyBilling')}
            subtitle={t('pages.Billing.invoicesPaymentsSubscriptions')}
            subtitleClassName={styles.heroSubtitle}
            cardClassName={styles.hero}
            titleContainerClassName={styles.titleContainer}
            titleClassName={styles.heroTitle}
          />

          {/* Quick Actions */}
          <IonCard className={styles.sectionCard}>
            <IonCardContent className={styles.section}>
              <IonLabel className={styles.sectionTitle}>{t('pages.Billing.quickActions')}</IonLabel>
              <IonList lines="none" className={styles.navRows}>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowNewInvoice(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={documentTextOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.createServiceOrderInvoice')}</h3>
                    <p>{t('pages.Billing.generateAndSendInvoiceToCustomer')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowInvoiceList(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={receiptOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.viewAllInvoices')}</h3>
                    <p>{t('pages.Billing.manageSentAndPendingInvoices')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowPriceCalculator(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                    <IonIcon icon={calculatorOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.priceCalculator')}</h3>
                    <p>{t('pages.Billing.estimateAServicePriceAndCreate')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={statsChartOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.financialReports')}</h3>
                    <p>{t('pages.Billing.revenueExpensesAndProfitAnalysis')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowPayments(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={walletOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.payments')}</h3>
                    <p>{t('pages.Billing.viewAndRecordCustomerPayments')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowServiceOrders(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                    <IonIcon icon={constructOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.serviceOrders')}</h3>
                    <p>{t('pages.Billing.manageServiceWorkOrders')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowEstimateList(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                    <IonIcon icon={receiptOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.estimates')}</h3>
                    <p>{t('pages.Billing.viewAndManageCustomerEstimates')}</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Customer Subscriptions */}
          <IonCard className={styles.sectionCard}>
            <IonCardContent className={styles.section}>
              <IonLabel className={styles.sectionTitle}>{t('pages.Billing.customerSubscriptions')}</IonLabel>
              <IonList lines="none" className={styles.navRows}>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowSubscriptions(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={calendarOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.activeSubscriptions')}</h3>
                    <p>{t('pages.Billing.viewAllActiveRecurringServices')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowPlans(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={settingsOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.subscriptionPlans')}</h3>
                    <p>{t('pages.Billing.manageServicePlansAndPricing')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                    <IonIcon icon={alertCircleOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.renewalsCancellations')}</h3>
                    <p>{t('pages.Billing.trackUpcomingRenewalsAndManageCancellati')}</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Billing Settings */}
          <IonCard className={styles.sectionCard}>
            <IonCardContent className={styles.section}>
              <IonLabel className={styles.sectionTitle}>{t('pages.Billing.billingSettings')}</IonLabel>
              <IonList lines="none" className={styles.navRows}>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={timerOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.defaultPaymentTerms')}</h3>
                    <p>{t('pages.Billing.setNet15Net30Or')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                    <IonIcon icon={warningOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.lateFeePolicy')}</h3>
                    <p>{t('pages.Billing.configureLateFeesForOverdueInvoices')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                    <IonIcon icon={calculatorOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.taxConfiguration')}</h3>
                    <p>{t('pages.Billing.setUpTaxRatesAndCalculations')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={mailOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.Billing.automatedReminders')}</h3>
                    <p>{t('pages.Billing.setUpPaymentReminderEmails')}</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>
        </IonGrid>
      </IonContent>

      <IonModal
        isOpen={showSearchModal}
        onDidDismiss={() => setShowSearchModal(false)}
        handle={false}
      >
        <SearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
        />
      </IonModal>

      <IonModal
        isOpen={showNewInvoice}
        onDidDismiss={() => setShowNewInvoice(false)}
        onWillDismiss={() => setShowNewInvoice(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <CreateInvoice onClose={() => setShowNewInvoice(false)} />
      </IonModal>

      <IonModal
        isOpen={showInvoiceList}
        onDidDismiss={() => setShowInvoiceList(false)}
        onWillDismiss={() => setShowInvoiceList(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <InvoiceList onClose={() => setShowInvoiceList(false)} />
      </IonModal>

      <IonModal
        isOpen={showPriceCalculator}
        onDidDismiss={() => setShowPriceCalculator(false)}
        onWillDismiss={() => setShowPriceCalculator(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <PriceCalculator onClose={() => setShowPriceCalculator(false)} />
      </IonModal>

      <IonModal
        isOpen={showEstimateList}
        onDidDismiss={() => setShowEstimateList(false)}
        onWillDismiss={() => setShowEstimateList(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <EstimateList onClose={() => setShowEstimateList(false)} />
      </IonModal>

      <IonModal
        isOpen={showServiceOrders}
        onDidDismiss={() => setShowServiceOrders(false)}
        onWillDismiss={() => setShowServiceOrders(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <ServiceOrderList onClose={() => setShowServiceOrders(false)} />
      </IonModal>

      <HelcimSubscriptionsModal
        isOpen={showSubscriptions}
        onClose={() => setShowSubscriptions(false)}
        onNavigateToContact={(contactId) => { setShowSubscriptions(false); setTimeout(() => router.push(`/tabs/contact/${contactId}`, 'forward', 'push'), 400); }}
      />

      <HelcimPlansModal
        isOpen={showPlans}
        onClose={() => setShowPlans(false)}
        onNavigateToContact={(contactId) => { setShowPlans(false); setTimeout(() => router.push(`/tabs/contact/${contactId}`, 'forward', 'push'), 400); }}
      />

      <HelcimPaymentsModal
        isOpen={showPayments}
        onClose={() => setShowPayments(false)}
      />
    </BasePageWithHeader>
  );

  return <IonPage className={styles.page}>{pageContent}</IonPage>;
};

export default BillingPage;
